import React, { useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { ChevronLeft, ChevronRight, Check } from "lucide-react";

const AvailabilityCalendar = ({
  currentDate,
  onNavigateMonth,
  viewMode,
  selectedMeals = [],
  localAvailability = {},
  changes = new Set(),
  onToggleAvailability,
  onDateClick,
  formatDate
}) => {
  const { t } = useTranslation();

  // Generate calendar for current month
  const calendar = useMemo(() => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - firstDay.getDay());
    
    const days = [];
    const current = new Date(startDate);
    
    for (let i = 0; i < 42; i++) {
      days.push(new Date(current));
      current.setDate(current.getDate() + 1);
    }
    
    return { days, firstDay, lastDay };
  }, [currentDate]);

  const isToday = useCallback((date) => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  }, []);

  const isCurrentMonth = useCallback((date) => {
    return date.getMonth() === currentDate.getMonth();
  }, [currentDate]);

  const renderCalendarDay = (date, index) => {
    const dateStr = formatDate(date);
    const inCurrentMonth = isCurrentMonth(date);
    const today = isToday(date);
    
    if (viewMode === "view") {
      // View mode: show indicator for days with availabilities
      const availabilities = localAvailability[dateStr] || {};
      const availableMealsCount = Object.keys(availabilities).length;
      const someAvailable = availableMealsCount > 0;
      
      return (
        <button
          key={index}
          onClick={() => inCurrentMonth && someAvailable && onDateClick(date, Object.values(availabilities))}
          disabled={!inCurrentMonth || !someAvailable}
          className={`
            relative p-3 text-sm font-medium rounded-md transition-all duration-200 min-h-[48px]
            ${!inCurrentMonth 
              ? 'text-gray-300 cursor-not-allowed' 
              : someAvailable
              ? 'text-blue-900 hover:bg-blue-100 cursor-pointer'
              : 'text-gray-700 cursor-default'
            }
            ${today ? 'ring-2 ring-blue-500' : ''}
            ${someAvailable 
              ? 'bg-blue-50 border border-blue-200' 
              : 'bg-gray-50'
            }
          `}
        >
          <span className="block">{date.getDate()}</span>
          {inCurrentMonth && someAvailable && (
            <div className="absolute top-1 right-1">
              <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
            </div>
          )}
          {inCurrentMonth && someAvailable && (
            <div className="absolute bottom-1 left-1/2 transform -translate-x-1/2">
              <span className="text-xs text-blue-700 font-medium">
                {availableMealsCount}
              </span>
            </div>
          )}
        </button>
      );
    } else {
      // Manage mode: existing logic
      const availableMealsCount = selectedMeals.filter(meal => 
        localAvailability[dateStr]?.[meal.id]?.available
      ).length;
      const hasChange = changes.has(dateStr);
      const allAvailable = selectedMeals.length > 0 && availableMealsCount === selectedMeals.length;
      const someAvailable = availableMealsCount > 0;

      return (
        <button
          key={index}
          onClick={() => inCurrentMonth && onToggleAvailability(date)}
          disabled={!inCurrentMonth}
          className={`
            relative p-3 text-sm font-medium rounded-md transition-all duration-200 min-h-[48px]
            ${!inCurrentMonth 
              ? 'text-gray-300 cursor-not-allowed' 
              : 'hover:bg-gray-50 cursor-pointer'
            }
            ${today ? 'ring-2 ring-blue-500' : ''}
            ${allAvailable 
              ? 'bg-green-100 text-green-800 hover:bg-green-200' 
              : someAvailable
              ? 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200'
              : 'text-gray-700'
            }
            ${hasChange ? 'ring-2 ring-orange-400' : ''}
          `}
        >
          <span className="block">{date.getDate()}</span>
          {inCurrentMonth && someAvailable && (
            <div className="absolute top-1 right-1">
              {allAvailable ? (
                <Check className="h-3 w-3 text-green-600" />
              ) : (
                <span className="text-xs font-bold text-yellow-700">
                  {availableMealsCount}
                </span>
              )}
            </div>
          )}
          {hasChange && (
            <div className="absolute -top-1 -right-1 w-2 h-2 bg-orange-500 rounded-full"></div>
          )}
        </button>
      );
    }
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      {/* Calendar Header */}
      <div className="flex items-center justify-between mb-6">
        <button
          onClick={() => onNavigateMonth(-1)}
          className="p-2 hover:bg-gray-100 rounded-md transition-colors"
        >
          <ChevronLeft className="h-5 w-5" />
        </button>
        
        <h2 className="text-lg font-semibold text-gray-900">
          {currentDate.toLocaleDateString(undefined, { 
            month: 'long', 
            year: 'numeric' 
          })}
        </h2>
        
        <button
          onClick={() => onNavigateMonth(1)}
          className="p-2 hover:bg-gray-100 rounded-md transition-colors"
        >
          <ChevronRight className="h-5 w-5" />
        </button>
      </div>

      {/* Day Headers */}
      <div className="grid grid-cols-7 gap-1 mb-2">
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
          <div key={day} className="p-2 text-center text-sm font-medium text-gray-500">
            {t(`mealAvailability.days.${day}`)}
          </div>
        ))}
      </div>

      {/* Calendar Grid */}
      <div className="grid grid-cols-7 gap-1">
        {calendar.days.map((date, index) => renderCalendarDay(date, index))}
      </div>

      {/* Legend */}
      <div className="flex items-center justify-center space-x-6 mt-6 text-sm text-gray-600">
        {viewMode === "manage" ? (
          <>
            <div className="flex items-center">
              <div className="w-4 h-4 bg-green-100 border border-green-300 rounded mr-2"></div>
              {t("mealAvailability.allAvailable")}
            </div>
            <div className="flex items-center">
              <div className="w-4 h-4 bg-yellow-100 border border-yellow-300 rounded mr-2"></div>
              {t("mealAvailability.partiallyAvailable")}
            </div>
            <div className="flex items-center">
              <div className="w-4 h-4 bg-gray-100 border border-gray-300 rounded mr-2"></div>
              {t("mealAvailability.unavailable")}
            </div>
            <div className="flex items-center">
              <div className="w-4 h-4 bg-orange-100 border border-orange-400 rounded mr-2"></div>
              {t("mealAvailability.hasChanges")}
            </div>
          </>
        ) : (
          <>
            <div className="flex items-center">
              <div className="w-4 h-4 bg-blue-50 border border-blue-200 rounded mr-2"></div>
              {t("mealAvailability.hasAvailabilities")}
            </div>
            <div className="flex items-center">
              <div className="w-4 h-4 bg-gray-50 border border-gray-300 rounded mr-2"></div>
              {t("mealAvailability.noAvailabilities")}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AvailabilityCalendar;
